import React from 'react';
import { motion } from 'framer-motion';
import {
  Cpu, UserCheck, History, BookOpenCheck, GitMerge, ServerCog,
} from 'lucide-react';
import { Reveal, StaggeredGroup, StaggeredItem } from './Reveal';

const items = [
  {
    icon: Cpu,
    title: 'AI-assisted, rules-driven',
    desc: 'Deterministic rules decide compliance state. AI surfaces context and guidance — never the final call.',
  },
  {
    icon: UserCheck,
    title: 'Human review recommended',
    desc: 'Every advisory output is presented for reviewer sign-off. No automated submissions to customs.',
  },
  {
    icon: History,
    title: 'Audit-ready validation history',
    desc: 'Every run captured with inputs, outputs, evidence, and reviewer decisions — exportable for audit.',
  },
  {
    icon: BookOpenCheck,
    title: 'Source-backed compliance guidance',
    desc: 'Recommendations cite EU regulations, customs circulars, and WCO trade rules by reference.',
  },
  {
    icon: GitMerge,
    title: 'Operational trade workflow',
    desc: 'Built around the reality of multi-document shipments: invoice, packing list, BL, CO, CoA.',
  },
  {
    icon: ServerCog,
    title: 'Enterprise-ready architecture',
    desc: 'Managed authentication, tenant-aware data model, and Postgres-backed audit trails from day one.',
  },
];

export default function SecuritySection() {
  return (
    <section id="security" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="https://images.unsplash.com/photo-1777789061958-c89abbd5d5a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjB0ZWNoJTIwYmFja2dyb3VuZCUyMGN5YW58ZW58MHx8fHwxNzc4MDgyMTgyfDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover opacity-[0.08]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#071120] via-[#071120]/90 to-[#071120]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <Reveal className="max-w-2xl mb-14 md:mb-20">
          <div className="text-xs uppercase tracking-[0.3em] text-cyan-glow font-mono mb-4">Trust & compliance</div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight">
            Enterprise-grade foundations, compliance-grade practice
          </h2>
          <p className="mt-4 text-slate-400 text-base md:text-lg leading-relaxed">
            ComplyTradeIQ is designed to operate inside regulated trade workflows — where deterministic rules, traceable evidence, and human review aren't optional.
          </p>
        </Reveal>

        <StaggeredGroup className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" stagger={0.06}>
          {items.map((it, i) => (
            <StaggeredItem key={it.title}>
              <motion.div
                whileHover={{ y: -4, borderColor: 'rgba(255, 255, 255, 0.2)' }}
                transition={{ duration: 0.25 }}
                className="rounded-xl border border-white/10 bg-navy-900/60 backdrop-blur-sm p-7 h-full transition-colors"
                data-testid={`security-${i}`}
              >
                <div className="h-10 w-10 rounded-lg bg-cyan-glow/10 border border-cyan-glow/20 grid place-items-center text-cyan-glow mb-5">
                  <it.icon className="h-5 w-5" />
                </div>
                <div className="font-display text-lg font-semibold text-white">{it.title}</div>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{it.desc}</p>
              </motion.div>
            </StaggeredItem>
          ))}
        </StaggeredGroup>

        {/* Compliance footer strip */}
        <Reveal delay={0.15}>
          <div className="mt-16 pt-8 border-t border-white/5 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
            <ComplianceTag label="Postgres-backed" sub="Audit-ready storage" />
            <ComplianceTag label="JWT auth" sub="Managed identity" />
            <ComplianceTag label="RLS by default" sub="Tenant-aware data" />
            <ComplianceTag label="Source citations" sub="Every recommendation" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ComplianceTag({ label, sub }) {
  return (
    <div className="flex flex-col">
      <span className="font-display text-white font-semibold tracking-tight">{label}</span>
      <span className="text-xs text-slate-500 mt-1 font-mono">{sub}</span>
    </div>
  );
}
